import { useEffect, useRef, useState } from "react";

import {
  Link,
  Button,
  TrafficBadge,
  cn,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Heading,
} from "@ogt/ui";

import type { Timetable } from "../types/FormTypes.js";

import { URLS } from "src/config/urls";

type Props = {
  timetables: Timetable[];
};

const ITEMS_PER_PAGE = 10;

function formatDate(d?: string) {
  if (!d) return "Okänt";

  return new Date(d).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function ResultList({ timetables }: Props) {
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const itemRefs = useRef<HTMLLIElement[]>([]);

  // Scroll to newly visible items
  useEffect(() => {
    if (visibleCount > ITEMS_PER_PAGE) {
      const firstNewIndex = visibleCount - ITEMS_PER_PAGE;
      const el = itemRefs.current[firstNewIndex];
      if (el) el.focus();
    }
  }, [visibleCount]);



  // Handle Show More
  const handleShowMore = () => setVisibleCount((prev) => prev + ITEMS_PER_PAGE);

  const visibleItems = timetables.slice(0, visibleCount);
  const hasMore = visibleCount < timetables.length;

  return (
    <>
      <hr />
      <ul className="list-none flex flex-col">
        {visibleItems.map((tt, index) => {

          const meta = {
            title: tt.trafficTypeName,
            iconCss: tt.trafficTypeIconCssClass,
            groupCss: tt.trafficTypeGroupCssClass,
          };
          return (
            <li
              className="mb-4"
              key={tt.id}
              tabIndex={-1}
              ref={(el) => {
                if (el) itemRefs.current[index] = el;
              }}
            >
              <Accordion type="multiple">
                <AccordionItem value={`item-${tt.id}`}>
                  <AccordionTrigger>
                    <div className="flex flex-col items-start gap-4">
                      <div className="flex gap-3 items-center ">
                        <div aria-hidden="true">
                          <TrafficBadge
                            key={tt.id}
                            meta={meta}
                            className={cn(
                              "lineicon",
                              meta?.iconCss,
                              tt.line === "Östgötapendeln" && "pendeln",
                              !meta?.iconCss && "animate-pulse opacity-60"
                            )}
                            titleOverride={meta?.title}
                          >
                            <span className="no-underline inline-block">
                              {tt.line === "Östgötapendeln"
                                ? tt.lineNrReal
                                : `${tt.line}`}
                            </span>
                          </TrafficBadge>
                        </div>
                        <Heading as="h3" className="mb-0! no-underline inline">
                          {tt.line === "Östgötapendeln"
                            ? tt.line
                            : `Linje ${tt.line}`}
                        </Heading>
                      </div>

                      <div>
                        <p className="mb-1 ">
                          <span className="no-underline! inline-block">
                            <strong>Kommun: </strong>
                            <span className="font-normal no-underline inline-block">
                              {tt.municipalities?.length
                                ? tt.municipalities
                                  .map((m) => m.name)
                                  .join(", ")
                                : tt.municipality}
                            </span>
                          </span>
                        </p>

                        <p className="mb-0.5">
                          <span className="no-underline! inline-block">
                            <strong>Linjesträckning: </strong>
                            <span className="font-normal">{tt.route}</span>
                          </span>
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent>
                    <div className="flex flex-col items-start gap-2">
                      <Heading as="h4" className="text-lg m-0 p-0">
                        Tidtabeller
                      </Heading>

                      {tt.trafficTypeKey === "GRPBÅT" && (
                        <>
                          <p className="mb-6">
                            Skärgårdstrafiken har särskilda tidtabeller som kan
                            ändras med kort varsel efter väder och årstid. Här
                            hittar du 

                            <Link
                              underline="always"
                              href={URLS.IslandFerryServices}
                              target="_blank"
                              icon="chevron-right"
                              iconPosition="right"
                            >
                              tidtabeller för skärgårdstrafiken.
                            </Link>

                          </p>
                        </>
                      )}

                      {tt.pdfs?.map((pdf, i) => {
                        const isFuture =
                          pdf.ValidFrom && new Date(pdf.ValidFrom) > new Date();

                        return (
                          <div className="mb-6 last:mb-0" key={i}>
                            {pdf.collectionPdf && (
                              <p className="mb-6">
                                I en samlingstabell finns flera linjer som går
                                samma eller liknande väg. Det gör att du kan se
                                alla avgångar på ett ställe och välja den som
                                passar dig bäst.
                              </p>
                            )}
                            {isFuture && (
                              <>
                                <hr />
                                <Heading as="h4">Kommande tidtabell</Heading>
                              </>
                            )}

                            <p className="mb-2 font-light">
                              Giltighetstid:
                              {formatDate(pdf.ValidFrom)} –{" "}
                              {formatDate(pdf.ValidTo)}
                            </p>

                            <div className="flex flex-col gap-2">
                              <Link
                                underline={"always"}
                                href={pdf.pdfUrl}
                                target="_blank"
                                icon="pdf"
                                iconPosition="left"
                              >
                                Öppna tidtabell som PDF
                              </Link>
                              {pdf.collectionPdf && (
                                <Link
                                  underline="always"
                                  href={pdf.collectionPdf.pdfUrl}
                                  target="_blank"
                                  icon="pdf"
                                  iconPosition="left"
                                >
                                  Öppna samlingstabell som PDF
                                </Link>
                              )}
                              {pdf.accessiblePdfUrl && (
                                <Link
                                  underline={"always"}
                                  href={pdf.accessiblePdfUrl}
                                  target="_blank"
                                  icon="a11y"
                                  iconPosition="left"
                                >
                                  Öppna tillgänglighetsanpassad tidtabell som
                                  PDF
                                </Link>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </li>
          );
        })}
      </ul>

      {hasMore && (
        <>
          <div className="mt-4 flex justify-start">
            <Button
              iconName="chevron-down"
              showIcon
              iconPosition="left"
              size="large"
              onClick={handleShowMore}
            >
              Visa fler tidtabeller
            </Button>
          </div>
        </>
      )}
    </>
  );
}
